/**
 * Safe loader for the HERE Maps JS SDK.
 *
 * IMPORTANT: We do NOT bundle `@here/maps-api-for-javascript` through webpack.
 * The SDK spawns Web Workers from a Blob URL using its own source at runtime.
 * When webpack processes the SDK, it rewrites internal `require()` calls to
 * `__webpack_require__()`, which then execute inside the worker context where
 * `__webpack_require__` does not exist — causing
 * `Uncaught ReferenceError: __webpack_require__ is not defined` and the map
 * fails to render in production builds.
 *
 * The safe approach is to load the official CDN scripts as plain <script>
 * tags so webpack never touches them and the SDK's worker code stays intact.
 *
 * The result is memoized so the SDK is only loaded once per session.
 */

const HERE_SDK_VERSION = 'v3/3.1';
const HERE_CDN_BASE = `https://js.api.here.com/${HERE_SDK_VERSION}`;
const HERE_CSS_URL = `${HERE_CDN_BASE}/mapsjs-ui.css`;

// Order matters: core must load before the others.
const HERE_SCRIPTS = [
    `${HERE_CDN_BASE}/mapsjs-core.js`,
    `${HERE_CDN_BASE}/mapsjs-service.js`,
    `${HERE_CDN_BASE}/mapsjs-mapevents.js`,
    `${HERE_CDN_BASE}/mapsjs-ui.js`,
];

let cachedH: any | null = null;
let pendingLoad: Promise<any | null> | null = null;

const loadScript = (src: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
            `script[data-here-sdk="${src}"]`,
        );
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () =>
                reject(new Error(`Failed to load HERE script: ${src}`)),
            );
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = false; // preserve execution order
        script.dataset.hereSdk = src;
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true';
            resolve();
        });
        script.addEventListener('error', () =>
            reject(new Error(`Failed to load HERE script: ${src}`)),
        );
        document.head.appendChild(script);
    });

const ensureStylesheet = (href: string): void => {
    if (document.querySelector(`link[data-here-sdk="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.hereSdk = href;
    document.head.appendChild(link);
};

const resolveH = (): any | null => {
    const win = typeof window !== 'undefined' ? (window as any) : undefined;
    const H = win?.H;
    if (
        H &&
        H.Map &&
        H.service?.Platform &&
        H.mapevents?.MapEvents &&
        H.map &&
        H.ui
    ) {
        return H;
    }
    return null;
};

export const loadHereMaps = async (): Promise<any | null> => {
    if (cachedH) return cachedH;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return null;
    }

    // SDK may already be present (e.g. preloaded in index.html).
    const existing = resolveH();
    if (existing) {
        cachedH = existing;
        return cachedH;
    }

    if (!pendingLoad) {
        pendingLoad = (async () => {
            try {
                ensureStylesheet(HERE_CSS_URL);
                // Load core first, then the rest in parallel.
                await loadScript(HERE_SCRIPTS[0]);
                await Promise.all(HERE_SCRIPTS.slice(1).map(loadScript));
                const H = resolveH();
                if (H) cachedH = H;
                return H;
            } catch {
                return null;
            } finally {
                if (!cachedH) pendingLoad = null;
            }
        })();
    }

    return pendingLoad;
};
